import React, { useState, useEffect } from "react";
import {
	Box,
	Paper,
	Typography,
	Table,
	TableBody,
	TableCell,
	TableContainer,
	TableHead,
	TableRow,
	Alert,
	Chip,
	TablePagination,
	TextField,
	InputAdornment,
	IconButton,
	Dialog,
	DialogTitle,
	DialogContent,
	DialogActions,
	Button,
	CircularProgress,
} from "@mui/material";
import {
	Search as SearchIcon,
	AlertTriangle as AlertTriangleIcon,
	MessageCircle as MessageIcon,
	Send as SendIcon,
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import dayjs from "dayjs";

const formatDate = (date) => {
	return date ? dayjs(date).format("DD/MM/YYYY") : "-";
};

const DelayedSchoolVisits = () => {
	const { user, supabase } = useAuth();
	const [delayedVisits, setDelayedVisits] = useState([]);
	const [filteredVisits, setFilteredVisits] = useState([]);
	const [loading, setLoading] = useState(true);
	const [errorMessage, setErrorMessage] = useState("");
	const [successMessage, setSuccessMessage] = useState("");
	const [page, setPage] = useState(0);
	const [rowsPerPage, setRowsPerPage] = useState(10);
	const [searchTerm, setSearchTerm] = useState("");

	// Reminder modal states
	const [openReminderDialog, setOpenReminderDialog] = useState(false);
	const [selectedVisit, setSelectedVisit] = useState(null);
	const [reminderForm, setReminderForm] = useState({
		subject: "",
		message: "",
	});
	const [sendingReminder, setSendingReminder] = useState(false);

	useEffect(() => {
		fetchDelayedVisits();
	}, []);

	useEffect(() => {
		applyFilters();
	}, [searchTerm, delayedVisits]);

	const fetchDelayedVisits = async () => {
		try {
			setLoading(true);
			const { data, error } = await supabase
				.from("school_visits")
				.select(
					`
					*,
					school_types (*),
					schools (*),
					planned_by_profile:profiles!planned_by (*),
					visited_by_profile:profiles!visited_by (*),
					visit_types (*),
					visit_status (*),
					sales_progression_stages (*)
				`
				)
				.order("planned_date", { ascending: false });

			if (error) throw error;

			// Filter for delayed visits (today > planned_date AND no visited_date)
			const today = new Date();
			today.setHours(0, 0, 0, 0);

			const delayed = (data || []).filter((visit) => {
				if (!visit.planned_date || visit.visited_date) return false;

				const plannedDate = new Date(visit.planned_date);
				plannedDate.setHours(0, 0, 0, 0);

				return today > plannedDate;
			});

			setDelayedVisits(delayed);
			setFilteredVisits(delayed);
		} catch (err) {
			console.error("Error fetching delayed visits:", err);
			setErrorMessage("Error loading delayed visits");
		} finally {
			setLoading(false);
		}
	};

	const applyFilters = () => {
		let filtered = [...delayedVisits];

		if (searchTerm) {
			const search = searchTerm.toLowerCase();
			filtered = filtered.filter(
				(visit) =>
					visit.schools?.name.toLowerCase().includes(search) ||
					visit.planned_by_profile?.full_name.toLowerCase().includes(search) ||
					visit.school_types?.name.toLowerCase().includes(search)
			);
		}

		setFilteredVisits(filtered);
	};

	const getDaysDelayed = (plannedDate) => {
		const today = new Date();
		const planned = new Date(plannedDate);
		const diffTime = today - planned;
		const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
		return diffDays;
	};

	const getSeverityColor = (daysDelayed) => {
		if (daysDelayed <= 3) return "warning";
		if (daysDelayed <= 7) return "error";
		return "error";
	};

	const openReminderModal = (visit) => {
		const daysDelayed = getDaysDelayed(visit.planned_date);
		setSelectedVisit(visit);
		setReminderForm({
			subject: `Reminder: Complete school visit to ${visit.schools?.name}`,
			message: `Dear ${visit.planned_by_profile?.full_name || "Team Member"},

This is a reminder that your scheduled visit to ${
				visit.schools?.name
			} was planned for ${formatDate(
				visit.planned_date
			)} and is now ${daysDelayed} day${daysDelayed === 1 ? "" : "s"} overdue.

Visit Details:
- School: ${visit.schools?.name}
- Visit Type: ${visit.visit_types?.name || "Standard Visit"}
- Planned Date: ${formatDate(visit.planned_date)}
- Days Delayed: ${daysDelayed}

Please complete this visit as soon as possible and update the system with the visit details.

If there are any issues or if you need to reschedule, please contact the admin team.

Thank you for your attention to this matter.`,
		});
		setOpenReminderDialog(true);
	};

	const closeReminderModal = () => {
		setSelectedVisit(null);
		setReminderForm({
			subject: "",
			message: "",
		});
		setOpenReminderDialog(false);
	};

	const handleSendReminder = async () => {
		if (!reminderForm.subject.trim() || !reminderForm.message.trim()) {
			setErrorMessage("Please fill in both subject and message");
			setTimeout(() => setErrorMessage(""), 3000);
			return;
		}

		try {
			setSendingReminder(true);

			const notification = {
				sender_id: user.id,
				recipient_id: selectedVisit.planned_by,
				subject: reminderForm.subject.trim(),
				message: reminderForm.message.trim(),
				type: "school_visit_reminder",
				is_read: false,
				created_at: new Date().toISOString(),
			};

			const { error } = await supabase
				.from("notifications")
				.insert(notification);

			if (error) throw error;

			setSuccessMessage(
				`Reminder sent to ${
					selectedVisit.planned_by_profile?.full_name || "staff member"
				} successfully`
			);
			closeReminderModal();

			setTimeout(() => {
				setSuccessMessage("");
			}, 3000);
		} catch (err) {
			console.error("Error sending reminder:", err);
			setErrorMessage("Failed to send reminder. Please try again.");
			setTimeout(() => {
				setErrorMessage("");
			}, 3000);
		} finally {
			setSendingReminder(false);
		}
	};

	const handleChangePage = (event, newPage) => {
		setPage(newPage);
	};

	const handleChangeRowsPerPage = (event) => {
		setRowsPerPage(parseInt(event.target.value, 10));
		setPage(0);
	};

	if (loading) {
		return (
			<Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
				<Typography>Loading delayed visits...</Typography>
			</Box>
		);
	}

	return (
		<Box>
			{errorMessage && (
				<Alert severity="error" sx={{ mb: 2 }}>
					{errorMessage}
				</Alert>
			)}

			{successMessage && (
				<Alert severity="success" sx={{ mb: 2 }}>
					{successMessage}
				</Alert>
			)}

			<Box
				sx={{
					display: "flex",
					justifyContent: "space-between",
					alignItems: "center",
					mb: 3,
				}}>
				<Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
					<AlertTriangleIcon size={24} color="#f57c00" />
					<Typography variant="h6" color="warning.main">
						Delayed School Visits ({filteredVisits.length})
					</Typography>
				</Box>

				<TextField
					size="small"
					placeholder="Search schools or staff"
					value={searchTerm}
					onChange={(e) => setSearchTerm(e.target.value)}
					InputProps={{
						startAdornment: (
							<InputAdornment position="start">
								<SearchIcon size={20} />
							</InputAdornment>
						),
					}}
					sx={{ minWidth: 250 }}
				/>
			</Box>

			{filteredVisits.length === 0 ? (
				<Alert severity="success" sx={{ textAlign: "center" }}>
					<Typography variant="h6">No delayed visits found!</Typography>
					<Typography variant="body2">
						All school visits are on track.
					</Typography>
				</Alert>
			) : (
				<Paper
					elevation={0}
					sx={{ border: "1px solid #e5e7eb", borderRadius: "8px" }}>
					<TableContainer>
						<Table>
							<TableHead>
								<TableRow>
									<TableCell sx={{ fontWeight: "bold" }}>Planned By</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>School</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>School Type</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>
										Planned Date
									</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>
										Days Delayed
									</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>Visit Type</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>
										Visit Status
									</TableCell>
									<TableCell sx={{ fontWeight: "bold" }}>
										Send Reminder
									</TableCell>
								</TableRow>
							</TableHead>
							<TableBody>
								{filteredVisits
									.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
									.map((visit) => {
										const daysDelayed = getDaysDelayed(visit.planned_date);
										return (
											<TableRow
												key={visit.id}
												sx={{
													backgroundColor: "#ffebee", // Light red background for delayed visits
													"&:hover": {
														backgroundColor: "#ffcdd2",
													},
												}}>
												<TableCell>
													{visit.planned_by_profile?.full_name}
												</TableCell>
												<TableCell>
													<Box
														sx={{
															display: "flex",
															alignItems: "center",
															gap: 1,
														}}>
														<AlertTriangleIcon size={16} color="#d32f2f" />
														{visit.schools?.name}
													</Box>
												</TableCell>
												<TableCell>{visit.school_types?.name}</TableCell>
												<TableCell>{formatDate(visit.planned_date)}</TableCell>
												<TableCell>
													<Chip
														label={`${daysDelayed} days`}
														color={getSeverityColor(daysDelayed)}
														size="small"
														variant="outlined"
													/>
												</TableCell>
												<TableCell>{visit.visit_types?.name}</TableCell>
												<TableCell>
													<Chip
														label={visit.visit_status?.name || "Pending"}
														color="warning"
														size="small"
													/>
												</TableCell>
												<TableCell>
													<IconButton
														size="small"
														color="primary"
														onClick={() => openReminderModal(visit)}
														title={`Send reminder to ${visit.planned_by_profile?.full_name}`}
														disabled={!visit.planned_by_profile}>
														<MessageIcon size={16} />
													</IconButton>
												</TableCell>
											</TableRow>
										);
									})}
							</TableBody>
						</Table>
					</TableContainer>

					<Box sx={{ display: "flex", justifyContent: "flex-end", p: 2 }}>
						<TablePagination
							component="div"
							count={filteredVisits.length}
							page={page}
							onPageChange={handleChangePage}
							rowsPerPage={rowsPerPage}
							onRowsPerPageChange={handleChangeRowsPerPage}
							rowsPerPageOptions={[10, 25, 50]}
						/>
					</Box>
				</Paper>
			)}

			{/* Send Reminder Dialog */}
			<Dialog
				open={openReminderDialog}
				onClose={closeReminderModal}
				maxWidth="md"
				fullWidth>
				<DialogTitle>
					<Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
						<MessageIcon size={24} />
						<Box>
							<Typography variant="h6">Send Visit Reminder</Typography>
							<Typography variant="body2" color="text.secondary">
								To:{" "}
								{selectedVisit?.planned_by_profile?.full_name || "Staff Member"}
							</Typography>
						</Box>
					</Box>
				</DialogTitle>
				<DialogContent dividers>
					<Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
						{/* Visit Details Summary */}
						<Paper
							elevation={0}
							sx={{ p: 2, bgcolor: "grey.50", borderRadius: 1 }}>
							<Typography variant="subtitle2" gutterBottom>
								Visit Details:
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>School:</strong> {selectedVisit?.schools?.name}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>Planned Date:</strong>{" "}
								{formatDate(selectedVisit?.planned_date)}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>Days Delayed:</strong>{" "}
								{selectedVisit ? getDaysDelayed(selectedVisit.planned_date) : 0}{" "}
								days
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>Visit Type:</strong>{" "}
								{selectedVisit?.visit_types?.name || "Standard Visit"}
							</Typography>
							<Typography variant="body2" color="text.secondary">
								<strong>School Type:</strong>{" "}
								{selectedVisit?.school_types?.name}
							</Typography>
						</Paper>

						<TextField
							label="Subject"
							value={reminderForm.subject}
							onChange={(e) =>
								setReminderForm({ ...reminderForm, subject: e.target.value })
							}
							fullWidth
							required
						/>

						<TextField
							label="Message"
							value={reminderForm.message}
							onChange={(e) =>
								setReminderForm({ ...reminderForm, message: e.target.value })
							}
							fullWidth
							multiline
							rows={8}
							required
							placeholder="Enter your reminder message..."
						/>
					</Box>
				</DialogContent>
				<DialogActions>
					<Button onClick={closeReminderModal} disabled={sendingReminder}>
						Cancel
					</Button>
					<Button
						onClick={handleSendReminder}
						variant="contained"
						disabled={
							!reminderForm.subject.trim() ||
							!reminderForm.message.trim() ||
							sendingReminder
						}
						startIcon={
							sendingReminder ? (
								<CircularProgress size={16} />
							) : (
								<SendIcon size={16} />
							)
						}>
						{sendingReminder ? "Sending..." : "Send Reminder"}
					</Button>
				</DialogActions>
			</Dialog>
		</Box>
	);
};

export default DelayedSchoolVisits;
